import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle, AlertCircle, FileSpreadsheet } from "lucide-react";

export default function Reconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // csv row index -> bool
  const fileRef = useRef();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/accounts");
      setAccounts(data);
      if (data.length && !accountId) setAccountId(data[0].id);
    })();
  }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!accountId) { toast.error("Seleziona un conto"); return; }
    setLoading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await fetch(`${API_BASE}/bank/reconcile?account_id=${accountId}`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setReport(data);
      setSelected(Object.fromEntries(data.unmatched_csv.map((_, i) => [i, true])));
      toast.success(`${data.totals.matched} match, ${data.totals.unmatched_csv} da importare`);
    } catch { toast.error("Errore riconciliazione"); }
    setLoading(false);
    e.target.value = "";
  };

  const importSelected = async () => {
    if (!report) return;
    const rows = report.unmatched_csv.filter((_, i) => selected[i]).map((r) => ({
      date: r.date, amount: r.abs_amount, type: r.type, description: r.description,
    }));
    if (rows.length === 0) { toast.error("Seleziona almeno una riga"); return; }
    try {
      const { data } = await api.post("/bank/import-rows", { account_id: accountId, rows });
      toast.success(`Importate ${data.imported} righe · delta ${fmtEUR(data.delta)}`);
      // reset
      setReport(null); setSelected({});
    } catch { toast.error("Errore import"); }
  };

  const toggleAll = () => {
    if (!report) return;
    const allOn = report.unmatched_csv.every((_, i) => selected[i]);
    setSelected(Object.fromEntries(report.unmatched_csv.map((_, i) => [i, !allOn])));
  };

  return (
    <div data-testid="reconciliation-page">
      <PageHeader eyebrow="Compliance finanziaria" title="Riconciliazione bancaria"
        subtitle="Confronta l'estratto conto CSV con i movimenti registrati e importa in un click quelli mancanti."
        actions={
          <div className="flex gap-2 items-center">
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="reco-account" className="w-72"><SelectValue placeholder="Conto…" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtEUR(a.balance)}</SelectItem>)}</SelectContent>
            </Select>
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={upload} data-testid="reco-input" />
            <Button data-testid="upload-csv-btn" onClick={() => fileRef.current?.click()} disabled={!accountId || loading}
              className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]">
              <Upload size={14} strokeWidth={1.5} /> {loading ? "Elaborazione…" : "Carica CSV"}
            </Button>
          </div>
        } />

      {!report ? (
        <div className="border border-dashed border-[#E2D9C8] rounded-lg p-10 text-center text-neutral-500">
          <FileSpreadsheet size={32} strokeWidth={1.2} className="mx-auto mb-3 text-neutral-400" />
          <p>Seleziona un conto e carica l'estratto conto in formato CSV.</p>
          <p className="text-xs mt-3">Colonne accettate: <code>date</code>/<code>data</code>, <code>amount</code>/<code>importo</code> (positivo = entrata, negativo = uscita), <code>description</code>/<code>causale</code>.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white">
              <div className="label-eyebrow">Righe CSV</div>
              <div className="font-serif text-3xl mt-2">{report.totals.csv_rows}</div>
              <div className="text-xs text-neutral-500 mt-1">{report.period.from} → {report.period.to}</div>
            </div>
            <div className="border border-emerald-300 rounded-lg p-4 bg-emerald-50" data-testid="stat-matched">
              <div className="flex items-center gap-1 label-eyebrow text-emerald-900"><CheckCircle2 size={12} /> Match</div>
              <div className="font-serif text-3xl mt-2 text-emerald-800">{report.totals.matched}</div>
            </div>
            <div className="border border-amber-300 rounded-lg p-4 bg-amber-50" data-testid="stat-unmatched">
              <div className="flex items-center gap-1 label-eyebrow text-amber-900"><AlertCircle size={12} /> Da importare</div>
              <div className="font-serif text-3xl mt-2 text-amber-800">{report.totals.unmatched_csv}</div>
            </div>
            <div className="border border-red-300 rounded-lg p-4 bg-red-50" data-testid="stat-orphan">
              <div className="flex items-center gap-1 label-eyebrow text-red-900"><XCircle size={12} /> Nel sistema ma non in CSV</div>
              <div className="font-serif text-3xl mt-2 text-red-800">{report.totals.unreconciled_db}</div>
            </div>
          </div>

          {report.unmatched_csv.length > 0 && (
            <div className="border border-[#E2D9C8] rounded-lg bg-white mb-6">
              <div className="p-4 flex justify-between items-center border-b border-[#E2D9C8]">
                <div>
                  <div className="label-eyebrow">Righe presenti nel CSV ma non nel gestionale</div>
                  <div className="text-xs text-neutral-500 mt-1">Seleziona quali importare come nuovi movimenti</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={toggleAll}>Seleziona tutte</Button>
                  <Button size="sm" onClick={importSelected} data-testid="import-selected"
                    className="bg-neutral-900 text-[#FDFBF7]">Importa selezionate</Button>
                </div>
              </div>
              <table className="w-full text-sm zebra">
                <thead><tr className="text-left label-eyebrow">
                  <th className="p-3 w-12"></th>
                  <th className="p-3">Data</th><th className="p-3">Descrizione</th>
                  <th className="p-3">Tipo</th><th className="p-3 text-right">Importo</th>
                </tr></thead>
                <tbody>
                  {report.unmatched_csv.map((r, i) => (
                    <tr key={i} className="border-t border-[#E2D9C8]" data-testid={`unmatched-${i}`}>
                      <td className="p-3">
                        <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected({ ...selected, [i]: !!v })} data-testid={`chk-unmatched-${i}`} />
                      </td>
                      <td className="p-3">{r.date}</td>
                      <td className="p-3 text-neutral-600">{r.description || "—"}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${r.type === "in" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{r.type === "in" ? "Entrata" : "Uscita"}</span></td>
                      <td className="p-3 text-right font-medium">{fmtEUR(r.abs_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.unreconciled_db.length > 0 && (
            <div className="border border-red-200 rounded-lg bg-red-50/40 mb-6">
              <div className="p-4 border-b border-red-200">
                <div className="label-eyebrow text-red-900">Movimenti nel gestionale non trovati nell'estratto CSV</div>
                <div className="text-xs text-neutral-500 mt-1">Verifica: potrebbero essere errori di data/importo, movimenti fantasma o mancanti dall'estratto</div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left label-eyebrow">
                  <th className="p-3">Data</th><th className="p-3">Descrizione</th>
                  <th className="p-3">Categoria</th><th className="p-3">Tipo</th><th className="p-3 text-right">Importo</th>
                </tr></thead>
                <tbody>
                  {report.unreconciled_db.map((t) => (
                    <tr key={t.id} className="border-t border-red-200" data-testid={`orphan-${t.id}`}>
                      <td className="p-3">{t.date}</td>
                      <td className="p-3 text-neutral-600">{t.description || "—"}</td>
                      <td className="p-3 text-xs capitalize text-neutral-500">{t.category}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${t.type === "in" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{t.type === "in" ? "Entrata" : "Uscita"}</span></td>
                      <td className="p-3 text-right font-medium">{fmtEUR(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.matched.length > 0 && (
            <details className="border border-[#E2D9C8] rounded-lg bg-[#F8F4EB]">
              <summary className="p-4 cursor-pointer label-eyebrow">Match trovati ({report.matched.length}) — clicca per espandere</summary>
              <table className="w-full text-sm bg-white">
                <thead><tr className="text-left label-eyebrow">
                  <th className="p-3">Data (CSV)</th><th className="p-3">Data (Gest.)</th>
                  <th className="p-3">Descrizione CSV</th><th className="p-3">Descrizione Gest.</th>
                  <th className="p-3 text-right">Importo</th>
                </tr></thead>
                <tbody>
                  {report.matched.map((m, i) => (
                    <tr key={i} className="border-t border-[#E2D9C8]">
                      <td className="p-3">{m.csv.date}</td>
                      <td className="p-3">{m.tx.date}</td>
                      <td className="p-3 text-neutral-600 text-xs">{m.csv.description || "—"}</td>
                      <td className="p-3 text-neutral-600 text-xs">{m.tx.description || "—"}</td>
                      <td className="p-3 text-right font-medium">{fmtEUR(m.csv.abs_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
}
